import React from 'react';
import { HBH_PARTNER } from '../../data/hbhEpisodes';

/**
 * HbhAttribution - "Sponsored by Home Building Hub" attribution chip.
 *
 * Purpose:
 *   Every place BuildPilot embeds a Home Building Hub video (episode pages,
 *   contextual embeds on guides, watch hub tiles) must carry a clear
 *   attribution back to HBH. This component is the single source of truth
 *   for that attribution. It also carries the HBH logo (subtle, monochrome
 *   on light backgrounds; small mark on dark).
 *
 * Variants:
 *   - `chip` (default): compact pill for above / below the video player.
 *   - `line`: single-line inline attribution for card grids.
 *   - `footer`: full-width row with logo + tagline, for the /watch hub.
 */
const HbhAttribution = ({ variant = 'chip', dark = false, className = '' }) => {
  if (variant === 'line') {
    // Rendered inside card-grid links (e.g. /watch tiles, RelatedEpisodeBand),
    // so this MUST be a non-anchor to avoid invalid nested <a>. The parent
    // card link handles navigation; this is a static attribution label.
    return (
      <span
        data-testid="hbh-attribution-line"
        className={`inline-flex items-center gap-1.5 text-[11px] ${dark ? 'text-white/70' : 'text-[#5a5a5f]'} ${className}`}
      >
        <span className="uppercase tracking-[0.07em] font-medium">Sponsored by</span>
        <img
          src={HBH_PARTNER.logo}
          alt="Home Building Hub"
          className="h-3.5 w-auto opacity-80"
          loading="lazy"
        />
      </span>
    );
  }

  if (variant === 'footer') {
    return (
      <div
        data-testid="hbh-attribution-footer"
        className={`rounded-2xl border ${dark ? 'border-white/10 bg-white/[0.03] text-white/85' : 'border-gray-200 bg-[#fafbfc] text-[#1d1d1f]'} p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 ${className}`}
      >
        <a
          href={HBH_PARTNER.homeUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="shrink-0"
        >
          <img
            src={HBH_PARTNER.logo}
            alt="Home Building Hub"
            className={`h-10 sm:h-12 w-auto ${dark ? 'opacity-90' : ''}`}
            loading="lazy"
          />
        </a>
        <div className="min-w-0 flex-1">
          <p className={`text-[10.5px] font-semibold uppercase tracking-[0.04em] ${dark ? 'text-white/60' : 'text-[#8a8a8f]'} mb-1`}>
            Sponsored content partner
          </p>
          <p className={`text-[13.5px] leading-[1.55] ${dark ? 'text-white/80' : 'text-[#4a4a4f]'}`}>
            All videos in the BuildPilot Watch library are produced by
            <a
              href={HBH_PARTNER.homeUrl}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className={`ml-1 font-semibold underline underline-offset-2 ${dark ? 'text-white hover:text-white/80' : 'text-[#1d1d1f] hover:text-[#1d8efe]'}`}
            >
              Home Building Hub
            </a>
            . {HBH_PARTNER.tagline} Content credit and copyright belongs to HBH.
          </p>
        </div>
      </div>
    );
  }

  // chip (default)
  return (
    <a
      href={HBH_PARTNER.homeUrl}
      target="_blank"
      rel="noopener noreferrer sponsored"
      data-testid="hbh-attribution-chip"
      className={`inline-flex items-center gap-2 rounded-full ${dark ? 'bg-white/10 hover:bg-white/15 text-white/90' : 'bg-[#f5f5f7] hover:bg-[#e9e9ec] text-[#1d1d1f]'} px-3 py-1.5 text-[11px] font-medium transition-colors ${className}`}
    >
      <span className={`uppercase tracking-[0.07em] ${dark ? 'text-white/60' : 'text-[#8a8a8f]'}`}>
        Sponsored by
      </span>
      <img
        src={HBH_PARTNER.logo}
        alt="Home Building Hub"
        className="h-3.5 w-auto"
        loading="lazy"
      />
    </a>
  );
};

export default HbhAttribution;
